"use client";

import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  Checkbox,
  Chip,
  Box,
} from "@mui/material";
import { CustomTextField, CustomFormLabel } from "../formsComponents";
import { api } from "../../api/api";
import type { IServiceItem } from "../../redux/service/service.types";
import type { IFileUpload } from "../FileUpload/FileUpload.type";
import { useBillingCompanyContext } from "../../providers";
import { getUniqueId } from "../../helpers";

const normalizeAttachments = (
  attachments: (IFileUpload | string)[] | null | undefined,
): IFileUpload[] => {
  if (!Array.isArray(attachments)) return [];
  return attachments.map((item) =>
    typeof item === "string"
      ? { file: null, key: getUniqueId(), path: item }
      : item,
  );
};

export interface IServicesAutoSearchProps {
  label?: string;
  heading?: string;
  disabled?: boolean;
  error?: string | boolean;
  helperText?: string;
  fullWidth?: boolean;
  multiple?: boolean;
  value: IServiceItem[] | IServiceItem | null;
  onSelect: (value: IServiceItem[] | IServiceItem | null) => void;
}

export const ServicesAutoSearch: React.FC<IServicesAutoSearchProps> = (
  props
) => {
  const {
    value,
    onSelect,
    label,
    disabled,
    fullWidth,
    heading,
    multiple = true,
  } = props;

  const company_outlet_uuid = useBillingCompanyContext();

  const [options, setOptions] = useState<readonly IServiceItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const url = `/service/get-service-item?company_outlet_uuid=${company_outlet_uuid.comapny.company_outlet_uuid}`;
        const res = await api.get(url);
        const data: IServiceItem[] = res.data.data;
        // Store attachments alongside the rest of the service item data so
        // they can later be aggregated into the rough estimate's attachments.
        setOptions(
          data.map((item) => ({
            ...item,
            attachments: normalizeAttachments(item.attachments),
          })),
        );
      } catch (error) {
        console.error("Error fetching services:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [company_outlet_uuid]);

  const getOptionLabel = (option: IServiceItem) => {
    return option.service_item_name || "";
  };

  const isOptionEqualToValue = (option: IServiceItem, value: IServiceItem) => {
    return option.service_item_uuid === value.service_item_uuid;
  };

  const handleChange = (
    event: any,
    newValue: IServiceItem[] | IServiceItem | null
  ) => {
    onSelect(newValue);
  };

  return (
    <>
      {heading && <CustomFormLabel>{heading}</CustomFormLabel>}
      <Autocomplete
        multiple={multiple}
        fullWidth={fullWidth}
        sx={{
          ".MuiOutlinedInput-root": {
            paddingTop: "0px",
            paddingBottom: "0px",
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
        }}
        getOptionLabel={getOptionLabel}
        options={options}
        value={value}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={loading}
        disabled={disabled}
        noOptionsText="No services available"
        onChange={handleChange}
        renderOption={(props, option, { selected }) => (
          <Box component="li" {...props}>
            {multiple && (
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
            )}
            {getOptionLabel(option)}
          </Box>
        )}
        renderTags={
          multiple
            ? (tagValue, getTagProps) =>
                (tagValue as IServiceItem[]).map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      label={getOptionLabel(option)}
                      onDelete={disabled ? undefined : tagProps.onDelete}
                    />
                  );
                })
            : undefined
        }
        renderInput={(params) => (
          <CustomTextField
            {...params}
            fullWidth
            label={label}
            placeholder={multiple ? "Select services..." : "Select service..."}
            value={params.inputProps.value}  
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
};
